import { ArrowRight, Facebook, Linkedin, Cookie, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import Logo from "./Logo";

const socials = [
    { icon: Facebook, label: "Facebook", href: "https://www.facebook.com/profile.php?id=61589107086129" },
    { icon: Linkedin, label: "LinkedIn", href: "https://www.linkedin.com/company/cleanydk" },
];

export default function Footer({ onCtaClick }) {
    return (
        <footer className="relative bg-slate-950 text-slate-300 overflow-hidden" data-testid="footer">
            {/* CTA Banner */}
            <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-sky-500 to-sky-700" />
                <div className="absolute inset-0 bg-grid opacity-20" />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-16 md:py-20 grid md:grid-cols-2 gap-8 items-center">
                    <div>
                        <h3 className="font-display text-3xl md:text-4xl font-bold text-white tracking-tight">
                            Klar til at modernisere driften?
                        </h3>
                        <p className="mt-3 text-sky-100 max-w-md">
                            Tilmeld dig launch — få tidlig adgang og bliv én af de første kunder på Cleany.
                        </p>
                    </div>
                    <div className="md:justify-self-end">
                        <button
                            onClick={onCtaClick}
                            data-testid="footer-cta-waitlist"
                            className="group inline-flex items-center gap-2 rounded-full bg-white text-sky-700 hover:bg-sky-50 font-semibold px-7 h-12 transition-all active:scale-[0.97] shadow-xl"
                        >
                            Tilmeld launch
                            <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Main footer */}
            <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-8">
                <div className="grid md:grid-cols-4 gap-10">
                    <div className="md:col-span-1">
                        <Logo variant="light" />
                        <p className="mt-4 text-sm text-slate-400 leading-relaxed">
                            Driftsplatformen til danske rengøringsfirmaer.
                        </p>
                        <div className="mt-5 flex items-center gap-2" data-testid="footer-socials">
                            {socials.map((s) => (
                                <a
                                    key={s.label}
                                    href={s.href}
                                    aria-label={s.label}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`footer-social-${s.label.toLowerCase()}`}
                                    className="h-9 w-9 rounded-full bg-white/5 border border-white/10 hover:bg-sky-500/20 hover:border-sky-400/40 hover:text-sky-300 text-slate-300 inline-flex items-center justify-center transition-colors"
                                >
                                    <s.icon className="h-4 w-4" strokeWidth={1.75} />
                                </a>
                            ))}
                        </div>
                    </div>
                    <FooterCol
                        title="Produkt"
                        links={[
                            { label: "Moduler", href: "/#moduler" },
                            { label: "Sådan virker det", href: "/#sadan-virker-det" },
                            { label: "Priser", href: "/#priser" },
                            { label: "FAQ", href: "/#faq" },
                            { label: "Updates", to: "/updates" },
                            {
                                label: "Log ind",
                                href: "https://my.cleany.dk",
                                external: true,
                            },
                        ]}
                    />
                    <FooterCol
                        title="Firma"
                        links={[
                            { label: "Om os", href: "/#om-os" },
                            { label: "Kontakt", href: "/#kontakt" },
                            { label: "Privatlivspolitik", to: "/privatlivspolitik" },
                            { label: "Handelsbetingelser", to: "/handelsbetingelser" },
                            { label: "Databehandleraftale", to: "/databehandleraftale" },
                            { label: "Cookies", to: "/cookies" },
                            { label: "Cleany Chat Privacy", to: "/chat/privacy" },
                            { label: "Cleany Chat Vilkår", to: "/chat/vilkaar" },
                        ]}
                    />
                    <FooterCol
                        title="Kontakt"
                        links={[
                            { label: "info@cleany.dk", href: "mailto:info@cleany.dk" },
                            { label: "+45 70 22 19 90", href: "tel:+4570221990" },
                            { label: "Strevelinsvej 16, 7000 Fredericia", href: "#" },
                        ]}
                    />
                </div>

                <div className="mt-12 pt-6 border-t border-slate-800 flex flex-col md:flex-row gap-3 items-center justify-between text-xs text-slate-500">
                    <div>
                        © {new Date().getFullYear()} Cleany.dk · CVR 45 42 27 20 · Alle rettigheder forbeholdes.
                    </div>
                    <div className="flex items-center gap-4">
                        <button
                            type="button"
                            onClick={() => window.dispatchEvent(new CustomEvent("cleany:open-cookie-settings"))}
                            data-testid="footer-cookie-settings"
                            className="inline-flex items-center gap-1.5 text-slate-400 hover:text-slate-200 transition-colors"
                        >
                            <Cookie className="h-3.5 w-3.5" />
                            Cookie-indstillinger
                        </button>
                        <Link
                            to="/admin/login"
                            data-testid="footer-admin-login"
                            className="inline-flex items-center gap-1.5 text-slate-400 hover:text-slate-200 transition-colors"
                        >
                            <Lock className="h-3.5 w-3.5" />
                            Admin
                        </Link>
                        <span className="hidden md:inline-flex items-center gap-2">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                            Pre-launch · Tilmelding åben
                        </span>
                    </div>
                </div>

                {/* Massive brand mark */}
                <div className="mt-12 pointer-events-none select-none">
                    <div className="font-display font-black text-[18vw] leading-none text-slate-900/90 tracking-tighter text-center">
                        Cleany.dk
                    </div>
                </div>
            </div>
        </footer>
    );
}

function FooterCol({ title, links }) {
    return (
        <div>
            <h4 className="text-white font-display font-semibold text-sm uppercase tracking-wider">
                {title}
            </h4>
            <ul className="mt-4 space-y-2.5">
                {links.map((l) => (
                    <li key={l.label}>
                        {l.to ? (
                            <Link
                                to={l.to}
                                className="text-sm text-slate-400 hover:text-white transition-colors"
                            >
                                {l.label}
                            </Link>
                        ) : (
                            <a
                                href={l.href}
                                target={l.external ? "_blank" : undefined}
                                rel={l.external ? "noopener noreferrer" : undefined}
                                onClick={l.onClick}
                                className="text-sm text-slate-400 hover:text-white transition-colors"
                            >
                                {l.label}
                            </a>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
}
